import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle2, Database, Filter, ListChecks, Target } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const steps = [
  {
    title: 'You describe your raise in plain English',
    detail:
      'You enter stage, industry, raise target, location preference, and 2-3 lines about what you are building. No long setup. No migration from other tools.',
  },
  {
    title: 'We filter out obvious non-fits first',
    detail:
      'Before ranking, we remove investors who do not invest at your stage, do not write checks in your range, or are focused on unrelated sectors.',
  },
  {
    title: 'We score each investor on concrete fit signals',
    detail:
      'Every remaining profile is scored using recent activity, check-size alignment, stage fit, and sector overlap. No generic "AI score" with no explanation.',
  },
  {
    title: 'We prioritize by likelihood of a real conversation',
    detail:
      'High-fit investors move to the top when they are actively deploying, have recent deals like yours, and have outreach paths founders can actually use.',
  },
  {
    title: 'You get a focused outreach list, not a giant spreadsheet',
    detail:
      'You receive a short list of perfect investors for your company with clear "why this fit" notes so you can send fewer emails with better targeting and better response odds.',
  },
];

const signals = [
  {
    signal: 'Recent investments',
    weCheck: 'Deals in the last 6-18 months in your category',
    why: 'Past behavior beats outdated portfolio pages.',
  },
  {
    signal: 'Check size history',
    weCheck: 'Typical first-check ranges and follow-on behavior',
    why: 'Stops you pitching a $10M-only fund for a $500K round.',
  },
  {
    signal: 'Stage alignment',
    weCheck: 'Pre-seed, seed, or early Series A patterns',
    why: 'Most ignored emails are stage mismatches.',
  },
  {
    signal: 'Industry focus',
    weCheck: 'Where they repeatedly deploy capital',
    why: 'A broad thesis is not the same as active conviction.',
  },
  {
    signal: 'Geography and market comfort',
    weCheck: 'Where they actually invest, not just what they claim',
    why: 'Many funds list "global" but stay region-heavy in practice.',
  },
  {
    signal: 'Contact path quality',
    weCheck: 'Reachable channels and practical outreach routes',
    why: 'A perfect fit is useless if you cannot get in front of them.',
  },
];

const prioritization = [
  { weight: 35, label: 'Stage + check-size fit (can they fund this round now?)' },
  { weight: 25, label: 'Recent deal relevance (are they active in your space?)' },
  { weight: 20, label: 'Sector depth (real pattern, not one-off bets)' },
  { weight: 10, label: 'Geography fit (where they actually deploy)' },
  { weight: 10, label: 'Reachability (can a founder realistically start a conversation)' },
];

const founderGets = [
  'A ranked list of high-fit investors (not a 5,000-name dump)',
  'Per-investor fit reason tied to specific signals',
  'Suggested outreach angle based on their recent behavior',
  'Contact routes so you can act immediately',
  'Perfect matches designed for the next 10-20 high-quality emails, not endless browsing',
];

export function HowItWorksPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isSubscribed } = useAuth();
  const { isDarkMode } = useTheme();
  const overlayBg = isDarkMode ? 'rgba(6, 9, 18, 0.82)' : 'rgba(220, 232, 255, 0.45)';
  const shouldReduceMotion = useReducedMotion();
  const [activeStep, setActiveStep] = useState(1);

  const stepsSectionRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress: stepProgress } = useScroll({
    target: stepsSectionRef,
    offset: ['start 75%', 'end 25%'],
  });
  const timelineFill = useTransform(stepProgress, [0, 1], ['0%', '100%']);

  useMotionValueEvent(stepProgress, 'change', (value) => {
    const current = Math.min(steps.length, Math.max(1, Math.ceil(value * steps.length)));
    setActiveStep(current);
  });

  const handleGetHighFitInvestors = () => {
    if (isAuthenticated && isSubscribed) {
      navigate('/matches');
    } else if (isAuthenticated && !isSubscribed) {
      navigate('/pricing');
    } else {
      navigate('/onboarding');
    }
  };

  const signalGridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
      },
    },
  };

  const signalCardVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 18 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <>
      <SEO
        title="How RaiseBeacon Works - High-Fit Investor Matching"
        description="Exactly how RaiseBeacon prioritizes investors: what data is analyzed, how matches are ranked, and what founders actually receive."
        canonicalUrl="https://backr.us/#/how-it-works"
      />
      <div className="min-h-screen relative">

        <section className="pt-28 pb-14 border-b border-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <p className="text-sm font-semibold tracking-wide text-[#2B7FFF] uppercase mb-3">
                How It Works
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground leading-tight mb-5">
                Most fundraising tools give you data.
                <br />
                We give you perfect matches you can actually use.
              </h1>
              <p className="max-w-3xl mx-auto text-lg text-muted-foreground leading-relaxed">
                If you are skeptical, good. You should be. Most founder tools are a prettier way to do the same spray-and-pray outreach. This page shows the exact mechanism so you can judge it yourself.
              </p>
            </motion.div>
          </div>
        </section>

        <section ref={stepsSectionRef} className="py-14 border-b border-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <ListChecks className="w-5 h-5 text-[#2B7FFF]" />
              <h2 className="text-2xl font-bold text-foreground">Step-by-step breakdown</h2>
            </div>

            <div className="mb-5 text-sm text-muted-foreground">
              Progress: <span className="text-foreground font-medium">{activeStep}/{steps.length}</span>
            </div>

            <div className="relative space-y-4 pl-6">
              <div className="absolute left-[13px] top-0 bottom-0 w-px bg-border" />
              <motion.div className="absolute left-[13px] top-0 w-px bg-[#2B7FFF]" style={{ height: timelineFill }} />
              {steps.map((step, idx) => (
                <motion.div
                  key={step.title}
                  initial={shouldReduceMotion ? false : { opacity: 0, y: 18 }}
                  whileInView={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="rounded-xl border border-border bg-card/80 p-5 sm:p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full font-semibold flex items-center justify-center shrink-0 transition-colors ${idx + 1 <= activeStep ? 'bg-[#2B7FFF] text-white' : 'bg-[#2B7FFF]/15 text-[#2B7FFF]'}`}>
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{step.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{step.detail}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 border-b border-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Database className="w-5 h-5 text-[#2B7FFF]" />
              <h2 className="text-2xl font-bold text-foreground">What data is analyzed</h2>
            </div>

            <motion.div
              variants={signalGridVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="grid md:grid-cols-2 gap-4"
            >
              {signals.map((item) => (
                <motion.div
                  key={item.signal}
                  variants={signalCardVariants}
                  className="rounded-xl border border-border bg-card/80 p-5 transition-transform hover:-translate-y-0.5"
                >
                  <h3 className="font-semibold text-foreground mb-3">{item.signal}</h3>
                  <p className="text-sm text-foreground/90 mb-2">
                    <span className="font-medium">What we check:</span> {item.weCheck}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground/90">Why it matters:</span> {item.why}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-14 border-b border-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Filter className="w-5 h-5 text-[#2B7FFF]" />
              <h2 className="text-2xl font-bold text-foreground">How matches are prioritized</h2>
            </div>

            <div className="rounded-xl border border-border bg-card/80 p-6">
              <p className="text-muted-foreground mb-4">
                We rank by expected fundraising usefulness, not by who has the biggest name. Bigger logo does not mean better fit.
              </p>
              <ul className="space-y-4">
                {prioritization.map((item) => (
                  <li key={item.label} className="text-foreground">
                    <div className="flex items-start gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-[#2B7FFF] mt-1 shrink-0" />
                      <span className="flex-1">{item.weight}% {item.label}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden ml-6">
                      <motion.div
                        className="h-full bg-[#2B7FFF]"
                        initial={shouldReduceMotion ? false : { width: 0 }}
                        whileInView={shouldReduceMotion ? {} : { width: `${item.weight}%` }}
                        viewport={{ once: true, amount: 0.6 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        style={shouldReduceMotion ? { width: `${item.weight}%` } : undefined}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-14 border-b border-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Target className="w-5 h-5 text-[#2B7FFF]" />
              <h2 className="text-2xl font-bold text-foreground">What founders actually receive</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {founderGets.map((item) => (
                <div key={item} className="rounded-xl border border-border bg-card/80 p-5 text-foreground">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              If your current process is "send more emails," this is the fix.
            </h2>
            <p className="text-muted-foreground mb-8">
              You do not need more investor names. You need fewer, better-fitting targets before you burn another week on outreach.
            </p>
            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={handleGetHighFitInvestors}
                size="lg"
                className="bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white px-8 py-6 text-lg font-medium rounded-xl"
              >
                Get 10 High-Fit Investors
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground">Takes 2 minutes. No credit card required.</p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default HowItWorksPage;
